import type { ApiMessage } from '../api/types';
import type { GlobalState } from '../global/types';

import { addCallback } from '../lib/teact/teactn';
import { getMessageText } from '../global/helpers';

import { deferRasiHeavyWork, shouldPauseRasiBackgroundWork } from './rasitechPerf';
import { scheduleCatalogSync } from './centralDb/syncCatalog';
import {
  classifyOutgoingMessage,
  describeMessageEvent,
  logUserEvent,
} from './userEventsStore';

const SCAN_INTERVAL_MS = 45_000;
const MAX_CHATS = 3;
const MAX_MESSAGES_PER_CHAT = 6;
const SEEN_MAX = 1500;

let lastScanAt = 0;
let scanScheduled = false;
const seenOutgoing = new Set<string>();

function scanOutgoing(global: GlobalState) {
  const userId = global.currentUserId;
  if (!userId) return;

  const activeIds = (global.chats.listIds.active || []).slice(0, MAX_CHATS);

  activeIds.forEach((chatId) => {
    const byId = global.messages.byChatId[chatId]?.byId;
    if (!byId) return;

    const messages = Object.values(byId)
      .filter((m) => m?.isOutgoing && !m.isTypingDraft)
      .slice(-MAX_MESSAGES_PER_CHAT);

    messages.forEach((message) => {
      const key = `${chatId}_${message.id}`;
      if (seenOutgoing.has(key)) return;
      seenOutgoing.add(key);
      if (seenOutgoing.size > SEEN_MAX) {
        const first = seenOutgoing.values().next().value;
        if (first) seenOutgoing.delete(first);
      }

      const eventType = classifyOutgoingMessage(message);
      if (!eventType) return;

      const chat = global.chats.byId[chatId];
      const title = chat?.title || chatId;

      void logUserEvent(userId, eventType, describeMessageEvent(message, title), {
        chatId,
        messageId: typeof message.id === 'number' ? message.id : undefined,
        peerTitle: title,
        meta: buildMessageMeta(message),
      });
    });
  });
}

function buildMessageMeta(message: ApiMessage): Record<string, string | number | boolean | undefined> {
  const text = getMessageText(message)?.text;
  const meta: Record<string, string | number | boolean | undefined> = {};
  if (text) meta.textPreview = text.slice(0, 80);
  const c = message.content;
  if (c?.mediaType === 'geo' || c?.mediaType === 'geoLive') {
    meta.lat = c.geo?.latitude;
    meta.lon = c.geo?.longitude;
  }
  if (c?.mediaType === 'contact' && c.contact) {
    meta.contactUserId = c.contact.userId;
  }
  return meta;
}

export function initUserMonitor() {
  addCallback((global: GlobalState) => {
    if (global.auth.state !== 'authorizationStateReady' || !global.currentUserId) return;
    if (shouldPauseRasiBackgroundWork(global)) return;

    const now = Date.now();
    if (now - lastScanAt < SCAN_INTERVAL_MS) return;
    if (scanScheduled) return;

    scanScheduled = true;
    lastScanAt = now;

    const snapshot = global;
    deferRasiHeavyWork(() => {
      scanScheduled = false;
      scanOutgoing(snapshot);
      scheduleCatalogSync(snapshot);
    });
  });
}
